import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { Elements } from '@stripe/react-stripe-js';
import { format } from 'date-fns';

import AppAlert from '../../../components/designe/app-alert/AppAlert';
import WarningNoticeBanner from '../../../components/designe/warning-notice-banner/WarningNoticeBanner';
import { useStyles as useWarningNoticeBannerStyles } from '../../../components/designe/warning-notice-banner/WarningNoticeBanner.styles';
import PageContainer from '../../../components/layout/page-container/PageContainer';
import {
  errorToastMessage,
  successToastMessage,
} from '../../../components/layout/toast-root/toast';
import ChoosePlan from '../../../components/reusable/choose-plan/ChoosePlan';
import { useGlobal } from '../../../context/GlobalContext';
import { useNavigationStack } from '../../../context/NavigationStackContext';
import { useThemeContext } from '../../../context/ThemeContext';
import envConfig from '../../../envConfig';
import { useFeature } from '../../../hooks/use-feature';
import {
  useCancelPlan,
  useSetupIntent,
  useSubscribeToPlan,
} from '../../../redux/api/paymentsApi';
import { useGetPlans } from '../../../redux/api/subscriptionsApi';
import { useGetMyDetails } from '../../../redux/api/users/usersApi';
import { AppFeatureKey } from '../../../shared/constants';
import { Paths } from '../../../shared/paths';
import { ISubscriptionPlan } from '../../../types/interfaces/subscription-plan';
import { getDateFnsLocale } from '../../../utils/datetime/date-time-utils';
import { buildStripeElementsOptions } from '../../../utils/payments/stripe-elements-options';
import { useStyles } from './UserPlan.styles';
import DonationBlock from './donation-block/DonationBlock';
import ExternalSupportBlock from './external-support-block/ExternalSupportBlock';
import PaymentWrapper from './payment-wrapper/PaymentWrapper';

const SUBSCRIPTION_CANCEL_SCHEDULED_KEY =
  'sg_subscription_cancel_scheduled_until';

function getSubscriptionMutationErrorMessage(
  error: unknown,
  t: (key: string) => string,
  defaultKey: string,
) {
  const status = (error as { status?: number })?.status;
  if (typeof status === 'number' && status >= 500 && status < 600) {
    return t('subscriptionPlan.subscriptionServiceUnavailableMessage');
  }
  return t(defaultKey);
}

function clearPendingCancellationStorage() {
  try {
    sessionStorage.removeItem(SUBSCRIPTION_CANCEL_SCHEDULED_KEY);
  } catch {
    // ignore
  }
}

export default function UserPlan(props) {
  const theme = useTheme();
  const { classes } = useStyles(theme);
  const { classes: warningBannerClasses } = useWarningNoticeBannerStyles(theme);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const planId = queryParams.get('plan');
  const { stripePromise } = useGlobal();
  const {
    [AppFeatureKey.subscriptions]: subscriptionsActive,
    [AppFeatureKey.donations]: donationsActive,
  } = useFeature();
  const { data: plans } = useGetPlans(undefined, {
    skip: !subscriptionsActive,
  });
  const [selectedPlan, setSelectedPlan] = useState<ISubscriptionPlan | null>(
    null,
  );
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [checkoutKind, setCheckoutKind] = useState<
    'subscription' | 'donation' | null
  >(null);
  const paymentElementErrorHandledRef = useRef(false);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [pendingPlanId, setPendingPlanId] = useState<string | null>(null);
  const [subscribeToPlan, { isLoading: isSubscribing }] = useSubscribeToPlan();
  const [setupIntent, { isLoading: isSetupIntentLoading }] = useSetupIntent();
  const [cancelPlan, { isLoading: isCanceling }] = useCancelPlan();
  const { theme: appThemeMode } = useThemeContext();
  const { data: user } = useGetMyDetails();
  const { navigate } = useNavigationStack();
  const { t, i18n } = useTranslation();

  const isPlanMutationLoading =
    isSubscribing || isSetupIntentLoading || isCanceling;

  const stripeElementsOptions = useMemo(() => {
    if (!clientSecret) {
      return null;
    }
    return buildStripeElementsOptions({
      clientSecret,
      muiTheme: theme,
      themeMode: appThemeMode,
      i18nLanguage: i18n.language,
    });
  }, [clientSecret, theme, appThemeMode, i18n.language]);

  useEffect(() => {
    paymentElementErrorHandledRef.current = false;
  }, [clientSecret]);

  useEffect(() => {
    if (!subscriptionsActive) {
      return;
    }
    if (plans && planId) {
      setSelectedPlan(plans.find((p) => p.id === planId) || null);
    }
  }, [plans, planId, subscriptionsActive]);

  useEffect(() => {
    if (user?.activeSubscription?.cancelAtPeriodEnd) {
      clearPendingCancellationStorage();
    }
  }, [user?.activeSubscription?.cancelAtPeriodEnd]);

  useEffect(() => {
    if (user && !user.activeSubscription) {
      clearPendingCancellationStorage();
    }
  }, [user]);

  const cancellationBannerDate = useMemo(() => {
    const sub = user?.activeSubscription;
    if (sub?.cancelAtPeriodEnd && sub.currentPeriodEnd) {
      return new Date(sub.currentPeriodEnd);
    }
    try {
      const raw = sessionStorage.getItem(SUBSCRIPTION_CANCEL_SCHEDULED_KEY);
      if (raw) {
        const parsed = new Date(raw);
        if (!Number.isNaN(parsed.getTime())) {
          return parsed;
        }
      }
    } catch {
      // ignore
    }
    return sub?.currentPeriodEnd ? new Date(sub.currentPeriodEnd) : null;
  }, [user?.activeSubscription]);

  const hasScheduledCancellation =
    Boolean(user?.activeSubscription?.cancelAtPeriodEnd) ||
    (typeof sessionStorage !== 'undefined' &&
      Boolean(sessionStorage.getItem(SUBSCRIPTION_CANCEL_SCHEDULED_KEY)));

  const showCancellationBanner =
    Boolean(user?.activeSubscription) &&
    Boolean(cancellationBannerDate) &&
    hasScheduledCancellation &&
    !bannerDismissed;

  function onPlanSelected(plan: ISubscriptionPlan) {
    setPendingPlanId(plan.id);

    if (
      user?.activeSubscription?.period === plan.period &&
      (user?.activeSubscription?.type as any) === plan.scope
    ) {
      const periodEnd = user?.activeSubscription?.currentPeriodEnd;
      cancelPlan()
        .unwrap()
        .then(() => {
          if (periodEnd) {
            try {
              sessionStorage.setItem(
                SUBSCRIPTION_CANCEL_SCHEDULED_KEY,
                new Date(periodEnd).toISOString(),
              );
            } catch {
              // ignore
            }
            const dateLabel = format(new Date(periodEnd), 'PPP', {
              locale: getDateFnsLocale(i18n.language),
            });
            successToastMessage(
              t('subscriptionPlan.subscriptionCancelScheduledMessage', {
                date: dateLabel,
              }),
            );
          } else {
            successToastMessage(
              t('subscriptionPlan.subscriptionCancelScheduledMessageNoDate'),
            );
          }
          navigate(Paths.root);
        })
        .catch((error: unknown) => {
          errorToastMessage(
            getSubscriptionMutationErrorMessage(
              error,
              t,
              'subscriptionPlan.subscriptionCancelErrorMessage',
            ),
          );
        })
        .finally(() => setPendingPlanId(null));
    } else if (user?.activeSubscription) {
      subscribeToPlan({ planId: plan.id })
        .unwrap()
        .then((data) => {
          if (data.clientSecret) {
            setSelectedPlan(plan);
            setCheckoutKind('subscription');
            setClientSecret(data.clientSecret);
            setPendingPlanId(null);
          } else {
            clearPendingCancellationStorage();
            successToastMessage(
              t('subscriptionPlan.subscriptionChangeSuccessMessage'),
            );
            navigate(Paths.root);
          }
        })
        .catch((error: unknown) => {
          errorToastMessage(
            getSubscriptionMutationErrorMessage(
              error,
              t,
              'subscriptionPlan.subscriptionChangeErrorMessage',
            ),
          );
        })
        .finally(() => setPendingPlanId(null));
    } else {
      setSelectedPlan(plan);
      setupIntent()
        .unwrap()
        .then(() =>
          subscribeToPlan({ planId: plan.id })
            .unwrap()
            .then((data) => {
              if (data.clientSecret) {
                setCheckoutKind('subscription');
                setClientSecret(data.clientSecret);
                setPendingPlanId(null);
              } else {
                clearPendingCancellationStorage();
                successToastMessage(
                  t('subscriptionPlan.subscriptionCreatedSuccessMessage'),
                );
                navigate(Paths.root);
              }
            }),
        )
        .catch((error: unknown) => {
          errorToastMessage(
            getSubscriptionMutationErrorMessage(
              error,
              t,
              'subscriptionPlan.subscriptionCreatedErrorMessage',
            ),
          );
        })
        .finally(() => setPendingPlanId(null));
    }
  }

  function onDonationIntentReady(secret: string) {
    setClientSecret(secret);
    setSelectedPlan(null);
  }

  function onCancel() {
    setClientSecret(null);
    setSelectedPlan(null);
    setCheckoutKind(null);
  }

  function onPaymentElementLoadError() {
    if (paymentElementErrorHandledRef.current) {
      return;
    }
    paymentElementErrorHandledRef.current = true;
    errorToastMessage(t('error.somethingWentWrong'));
    setClientSecret(null);
    setSelectedPlan(null);
    setCheckoutKind(null);
    setPendingPlanId(null);
  }

  const showTestModeBanner = envConfig.environment !== 'prod';

  return (
    <PageContainer
      title={
        subscriptionsActive
          ? t('subscriptionPlan.userPlan')
          : t('menu.projectSupport')
      }>
      <div className={classes.subscriptionPlanWrapper}>
        {showTestModeBanner && (
          <WarningNoticeBanner
            title={t('subscriptionPlan.testModeBannerTitle')}>
            <Trans
              i18nKey="subscriptionPlan.testModeBannerBody"
              components={{
                card: (
                  <Box
                    component="span"
                    className={warningBannerClasses.inlineCode}
                  />
                ),
              }}
            />
          </WarningNoticeBanner>
        )}
        {subscriptionsActive &&
          showCancellationBanner &&
          cancellationBannerDate && (
            <AppAlert
              severity="info"
              onClose={() => setBannerDismissed(true)}
              sx={{ mb: 2 }}>
              {t('subscriptionPlan.subscriptionCancellationScheduledBanner', {
                date: format(cancellationBannerDate, 'PPP', {
                  locale: getDateFnsLocale(i18n.language),
                }),
              })}
            </AppAlert>
          )}
        {!clientSecret && (
          <>
            {subscriptionsActive && (
              <ChoosePlan
                selectedPlan={selectedPlan}
                onPlanSelected={onPlanSelected}
                currentPlan={user?.activeSubscription}
                pendingPlanId={pendingPlanId}
                isPlanMutationLoading={isPlanMutationLoading}
              />
            )}
            {donationsActive && (
              <>
                <DonationBlock onPaymentIntentReady={onDonationIntentReady} />
                <ExternalSupportBlock />
              </>
            )}
          </>
        )}
        <div className={classes.paymentElementWrapper}>
          {clientSecret && stripeElementsOptions && (
            <Elements
              key={`${clientSecret}-${appThemeMode}`}
              stripe={stripePromise}
              options={stripeElementsOptions}>
              <PaymentWrapper
                onCancel={onCancel}
                checkoutKind={checkoutKind ?? 'subscription'}
                onPaymentElementLoadError={onPaymentElementLoadError}
              />
            </Elements>
          )}
        </div>
      </div>
    </PageContainer>
  );
}
